import { useEffect, useRef, useState } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The sidebar as a drawer on narrow screens: whether it is open, the refs its focus handling
 * needs, and the two effects that answer a navigation and an open drawer.
 *
 * A navigation closes the drawer and moves focus to the new page's heading — one effect, because
 * both answer the same event and the drawer has to be shut before the heading takes the focus.
 */
export function useCabinetDrawer(pathname: string) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);
  const drawerToggleRef = useRef<HTMLButtonElement>(null);
  const isFirstRender = useRef(true);

  // Focus the new page's heading on navigation (not on first mount, and never via
  // scrollIntoView — .focus() is the real a11y signal screen readers need).
  useEffect(() => {
    setDrawerOpen(false);
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Pages that fetch their own data (e.g. ProfilePage) render a loading placeholder
    // with no <h1> for a beat before the real heading mounts — focus it whenever it
    // actually appears rather than only checking once, right after navigation.
    //
    // Only a heading that is rendered counts. The pages are lazy: while the next one loads, React
    // keeps the previous page in the DOM under display: none beside the fallback, and its <h1>
    // cannot take the focus — taking it for the new page's left the observer never attached.
    const main = document.getElementById('main');
    if (!main) return;
    const focusHeading = () => {
      const heading = Array.from(main.querySelectorAll<HTMLElement>('h1')).find(
        (h) => h.getClientRects().length > 0,
      );
      if (!heading) return false;
      heading.setAttribute('tabindex', '-1');
      heading.focus();
      return document.activeElement === heading;
    };
    if (focusHeading()) return;
    const observer = new MutationObserver(() => {
      if (focusHeading()) observer.disconnect();
    });
    observer.observe(main, { childList: true, subtree: true });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Drawer: Escape closes it, Tab is trapped inside while open, body scroll is locked.
  useEffect(() => {
    if (!drawerOpen) return;
    const drawer = drawerRef.current;
    const focusables = drawer
      ? Array.from(drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      : [];
    focusables[0]?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDrawerOpen(false);
        drawerToggleRef.current?.focus();
        return;
      }
      if (e.key === 'Tab' && focusables.length > 0) {
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [drawerOpen]);

  return {
    drawerOpen,
    drawerRef,
    drawerToggleRef,
    /** The header's menu button. */
    toggle: () => setDrawerOpen((v) => !v),
    /** A click on the overlay beside the drawer. */
    close: () => setDrawerOpen(false),
    /** The drawer's own close button: shut it and hand the focus back to the menu button. */
    closeAndRefocus: () => {
      setDrawerOpen(false);
      drawerToggleRef.current?.focus();
    },
  };
}
